/**
 * On-demand content hooks for lesson quiz, glossary, extras.
 *
 * Provides hooks for:
 * - Fetching on-demand content with polling while processing
 * - Triggering generation of on-demand content
 * - Retrying failed generation
 *
 * Phase 7 retired flashcards - the in-lesson Highlights carousel
 * covers the same use case and feeds the global mindspace FSRS state.
 */

import { useState } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  lessonApi,
  type OnDemandContentType,
  type LessonQuiz,
  type LessonFlashcards,
  type LessonGlossary,
  type LessonExtras,
  type LessonConcepts,
  type LessonText,
  type LessonVideo,
  type LessonMindmap,
  type LessonHighlightsResponse,
  type StepActionResponse,
  type AddExtrasAttachmentRequest,
  type LinkPreviewResponse,
} from '@/lib/api-client';
import type { PipelineStatusResponse, StepState } from '@/types/pipeline';
import { queryKeys } from '@/config/query-client';
import { track } from '@/config/observability';
import { notify } from '@/lib/notify';
import { handleStepConflict, isStepConflict } from '@/lib/stepConflict';

/** Mutations retry once by default; a 409 (nothing to re-run) never will
 *  succeed on a second try, so don't send it twice. */
const retryUnlessConflict = (failureCount: number, err: Error) =>
  !isStepConflict(err) && failureCount < 1;

/**
 * Write `queued` onto one pipeline step in the cached status, then refetch.
 * The cancel comes first (and is awaited: a cancelled fetch reverts the cache
 * on a later tick) so a poll that left before the POST cannot land after us
 * with the stale not_started state.
 */
async function markStepQueued(
  queryClient: QueryClient,
  lessonId: string,
  step: string
): Promise<void> {
  const key = queryKeys.lessons.pipelineStatus(lessonId);
  await queryClient.cancelQueries({ queryKey: key });
  const prev = queryClient.getQueryData<PipelineStatusResponse>(key);
  const view = prev?.steps?.[step];
  if (prev && view && view.state === 'not_started') {
    queryClient.setQueryData<PipelineStatusResponse>(key, {
      ...prev,
      steps: {
        ...prev.steps,
        [step]: { ...view, state: 'queued' as StepState, error: null },
      },
    });
  }
  void queryClient.invalidateQueries({ queryKey: key });
}

/** Optional gating for the auto-generated artifact hooks - lets parents
 *  defer polling until a pipeline step is at least `processing`. */
export interface ArtifactHookOptions {
  enabled?: boolean;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const onDemandKeys = {
  all: ['on-demand'] as const,
  quiz: (lessonId: string) => [...onDemandKeys.all, 'quiz', lessonId] as const,
  flashcards: (lessonId: string) =>
    [...onDemandKeys.all, 'flashcards', lessonId] as const,
  glossary: (lessonId: string) =>
    [...onDemandKeys.all, 'glossary', lessonId] as const,
  extras: (lessonId: string) =>
    [...onDemandKeys.all, 'extras', lessonId] as const,
  concepts: (lessonId: string) =>
    [...onDemandKeys.all, 'concepts', lessonId] as const,
  // Auto-generated artifacts (driven by the lesson's flow). Same hook
  // shape as the on-demand artifacts above - the split between auto vs.
  // on-demand is a backend concern; the frontend just polls each.
  text: (lessonId: string) => [...onDemandKeys.all, 'text', lessonId] as const,
  mindmap: (lessonId: string) =>
    [...onDemandKeys.all, 'mindmap', lessonId] as const,
  video: (lessonId: string) =>
    [...onDemandKeys.all, 'video', lessonId] as const,
  highlights: (lessonId: string) =>
    [...onDemandKeys.all, 'highlights', lessonId] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook for fetching quiz content. Polls every 3s while processing.
 */
export function useQuiz(lessonId: string | undefined) {
  const query = useQuery<LessonQuiz>({
    queryKey: onDemandKeys.quiz(lessonId || ''),
    queryFn: () => lessonApi.getQuiz(lessonId!),
    enabled: !!lessonId,
    staleTime: 30 * 1000,
    refetchInterval: query => {
      const data = query.state.data as LessonQuiz | undefined;
      if (!data) return false;
      return data.status === 'processing' || data.status === 'pending'
        ? 3000
        : false;
    },
  });

  return {
    data: query.data,
    questions: query.data?.questions ?? [],
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching flashcards content. Polls every 3s while processing.
 */
export function useFlashcards(lessonId: string | undefined) {
  const query = useQuery<LessonFlashcards>({
    queryKey: onDemandKeys.flashcards(lessonId || ''),
    queryFn: () => lessonApi.getFlashcards(lessonId!),
    enabled: !!lessonId,
    staleTime: 30 * 1000,
    refetchInterval: query => {
      const data = query.state.data as LessonFlashcards | undefined;
      if (!data) return false;
      return data.status === 'processing' || data.status === 'pending'
        ? 3000
        : false;
    },
  });

  return {
    data: query.data,
    cards: query.data?.cards ?? [],
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching glossary content. Polls every 3s while processing.
 */
export function useGlossary(lessonId: string | undefined) {
  const query = useQuery<LessonGlossary>({
    queryKey: onDemandKeys.glossary(lessonId || ''),
    queryFn: () => lessonApi.getGlossary(lessonId!),
    enabled: !!lessonId,
    staleTime: 30 * 1000,
    refetchInterval: query => {
      const data = query.state.data as LessonGlossary | undefined;
      if (!data) return false;
      return data.status === 'processing' || data.status === 'pending'
        ? 3000
        : false;
    },
  });

  return {
    data: query.data,
    terms: query.data?.terms ?? [],
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching extras (manually-curated attachments). No polling
 * - extras are user-curated, never async-generated.
 */
export function useExtras(lessonId: string | undefined) {
  const query = useQuery<LessonExtras>({
    queryKey: onDemandKeys.extras(lessonId || ''),
    queryFn: () => lessonApi.getExtras(lessonId!),
    enabled: !!lessonId,
    staleTime: 30 * 1000,
  });

  return {
    data: query.data,
    attachments: query.data?.attachments ?? [],
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

/**
 * Mutations for adding/removing Extras attachments. Each mutation
 * invalidates the extras query so the list re-renders immediately.
 */
export function useAddExtrasAttachment(lessonId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<LessonExtras, Error, AddExtrasAttachmentRequest>({
    mutationFn: body => lessonApi.addExtrasAttachment(lessonId!, body),
    onSuccess: data => {
      // Surgical update - write the fresh extras directly into the
      // cache so the UI flips before the refetch round-trip.
      if (lessonId) {
        queryClient.setQueryData(onDemandKeys.extras(lessonId), data);
      }
    },
  });
}

export function useDeleteExtrasAttachment(lessonId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<LessonExtras, Error, string>({
    mutationFn: attachmentId =>
      lessonApi.deleteExtrasAttachment(lessonId!, attachmentId),
    onSuccess: data => {
      if (lessonId) {
        queryClient.setQueryData(onDemandKeys.extras(lessonId), data);
      }
    },
  });
}

export function usePreviewLink() {
  return useMutation<LinkPreviewResponse, Error, string>({
    mutationFn: url => lessonApi.previewExtrasLink(url),
  });
}

/**
 * Hook for fetching concepts content.
 */
export function useConcepts(lessonId: string | undefined) {
  const query = useQuery<LessonConcepts>({
    queryKey: onDemandKeys.concepts(lessonId || ''),
    queryFn: () => lessonApi.getConcepts(lessonId!),
    enabled: !!lessonId,
    staleTime: 30_000,
    refetchInterval: query => {
      const data = query.state.data as LessonConcepts | undefined;
      if (!data) return false;
      return data.status === 'processing' || data.status === 'pending'
        ? 3000
        : false;
    },
  });

  return {
    data: query.data,
    concepts: query.data?.concepts ?? [],
    links: query.data?.links ?? [],
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

// ============================================================================
// AUTO-GENERATED ARTIFACT HOOKS - text / mindmap / video / highlights
// ============================================================================
//
// These artifacts are produced by the lesson's flow (Phase 3 fan-out) - the
// frontend doesn't trigger them, just polls for completion. The hooks
// follow the same shape as the on-demand artifacts above so panels can
// stay consistent: `useX(lessonId, { enabled })` → `{data, status, ...}`.
//
// `enabled` lets a parent defer the actual GET until pipelineStatus says
// the step has at least started - saves wasted polls on every lesson load.

/**
 * Hook for fetching the markdown lesson text. Polls every 3s while
 * processing; stops on completed/failed.
 */
export function useLessonText(
  lessonId: string | undefined,
  { enabled = true }: ArtifactHookOptions = {}
) {
  const query = useQuery<LessonText>({
    queryKey: onDemandKeys.text(lessonId || ''),
    queryFn: () => lessonApi.getLessonText(lessonId!),
    enabled: enabled && !!lessonId,
    staleTime: 30 * 1000,
    refetchInterval: query => {
      const data = query.state.data as LessonText | undefined;
      if (!data) return false;
      return data.status === 'processing' || data.status === 'pending'
        ? 3000
        : false;
    },
  });

  return {
    data: query.data,
    content: query.data?.content ?? '',
    wordCount: query.data?.word_count ?? 0,
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching the markdown mindmap. Polls every 3s while processing.
 */
export function useLessonMindmap(
  lessonId: string | undefined,
  { enabled = true }: ArtifactHookOptions = {}
) {
  const query = useQuery<LessonMindmap>({
    queryKey: onDemandKeys.mindmap(lessonId || ''),
    queryFn: () => lessonApi.getLessonMindmap(lessonId!),
    enabled: enabled && !!lessonId,
    staleTime: 30 * 1000,
    refetchInterval: query => {
      const data = query.state.data as LessonMindmap | undefined;
      if (!data) return false;
      return data.status === 'processing' || data.status === 'pending'
        ? 3000
        : false;
    },
  });

  return {
    data: query.data,
    markdownContent: query.data?.markdown_content ?? '',
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching the lesson video. Polls every 3s while processing -
 * Manim runs are slow (2-5 min), so the parent typically wants a long-
 * running spinner here regardless.
 */
export function useLessonVideo(
  lessonId: string | undefined,
  { enabled = true }: ArtifactHookOptions = {}
) {
  const query = useQuery<LessonVideo>({
    queryKey: onDemandKeys.video(lessonId || ''),
    queryFn: () => lessonApi.getLessonVideo(lessonId!),
    enabled: enabled && !!lessonId,
    staleTime: 30 * 1000,
    refetchInterval: query => {
      const data = query.state.data as LessonVideo | undefined;
      if (!data) return false;
      return data.status === 'processing' ? 5000 : false;
    },
  });

  return {
    data: query.data,
    videoUrl: query.data?.video_url,
    duration: query.data?.duration ?? 0,
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    scenes: query.data?.scenes ?? [],
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching per-concept highlight carousels. Polls every 3s
 * while the highlights task is running.
 */
export function useLessonHighlights(
  lessonId: string | undefined,
  { enabled = true }: ArtifactHookOptions = {}
) {
  const query = useQuery<LessonHighlightsResponse>({
    queryKey: onDemandKeys.highlights(lessonId || ''),
    queryFn: () => lessonApi.getHighlights(lessonId!),
    enabled: enabled && !!lessonId,
    staleTime: 30 * 1000,
    refetchInterval: query => {
      const data = query.state.data as LessonHighlightsResponse | undefined;
      if (!data) return false;
      return data.status === 'processing' || data.status === 'pending'
        ? 3000
        : false;
    },
  });

  return {
    data: query.data,
    carousels: query.data?.carousels ?? [],
    status: query.data?.status ?? 'not_started',
    error: query.data?.error,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}

// ============================================================================
// GENERATION + RETRY MUTATIONS (legacy on-demand pattern)
// ============================================================================

/**
 * Hook for triggering on-demand content generation.
 *
 * `pendingType` exposes which content type is currently in flight so
 * call sites can scope their loading UI - without it, the shared
 * `isPending` flag bleeds across sibling tabs (e.g. clicking
 * "Generate Flashcards" would also light up the Quiz tab's loader).
 *
 * We track `pendingType` via explicit local state set in `onMutate` and
 * cleared in `onSettled` rather than deriving from `mutation.variables`.
 * React Query keeps `variables` set after a successful mutation, so a
 * second click on a different content type can briefly read the prior
 * type's variables during the state-transition render - that race
 * caused a phantom loading-screen flicker on sibling tabs.
 */
export function useGenerateContent(lessonId: string) {
  const queryClient = useQueryClient();
  const [pendingType, setPendingType] = useState<OnDemandContentType | null>(
    null
  );

  const mutation = useMutation<StepActionResponse, Error, OnDemandContentType>({
    mutationFn: contentType => lessonApi.generateContent(lessonId, contentType),
    // Never re-send a generate: a timed-out POST may already have dispatched
    // the job server-side, and a second try would queue it twice.
    retry: false,
    onMutate: contentType => {
      setPendingType(contentType);
    },
    // Own onError replaces the global toast, so re-issue it for real errors.
    // A 409 means "already generated" - refresh to show it, calmly.
    onError: err => {
      if (!handleStepConflict(err, queryClient, lessonId)) notify.error(err);
    },
    onSettled: () => {
      setPendingType(null);
    },
    onSuccess: async (_data, contentType) => {
      track('content_generated', {
        lesson_id: lessonId,
        content_type: contentType,
      });
      // Invalidate the specific content query to trigger polling
      const keyMap: Record<OnDemandContentType, readonly string[]> = {
        quiz: onDemandKeys.quiz(lessonId),
        flashcards: onDemandKeys.flashcards(lessonId),
        glossary: onDemandKeys.glossary(lessonId),
        video: ['lessons', 'video', lessonId],
        // The carousel uses a dedicated query key (`queryKeys.lessons.highlights(id)`)
        // - invalidate that, not `onDemandKeys.highlights`, so the panel
        // actually refetches after generate/retry.
        highlights: queryKeys.lessons.highlights(lessonId),
      };
      queryClient.invalidateQueries({ queryKey: keyMap[contentType] });
      // The backend has marked the step queued, but the cached status still
      // says not_started. Write queued now - before onSettled clears
      // pendingType - so the gate goes straight from busy to the queued
      // skeleton and the Generate button never flashes back. Awaited: React
      // Query runs onSettled only after this resolves.
      await markStepQueued(queryClient, lessonId, contentType);
    },
  });

  return { ...mutation, pendingType };
}

/**
 * Hook for retrying failed on-demand content generation.
 *
 * Mirrors `useGenerateContent` and exposes `pendingType` for scoped
 * retry-loading UI. See note on `useGenerateContent` for why we track
 * `pendingType` via local state instead of `mutation.variables`.
 */
export function useRetryContent(lessonId: string) {
  const queryClient = useQueryClient();
  const [pendingType, setPendingType] = useState<OnDemandContentType | null>(
    null
  );

  // The per-artifact content query key. The carousel uses a dedicated key
  // (`queryKeys.lessons.highlights(id)`) and video a legacy one (`['lessons','video',id]`),
  // so this map is the single source of truth for both optimistic writes and
  // post-success invalidation.
  const contentKey = (type: OnDemandContentType): readonly string[] =>
    ({
      quiz: onDemandKeys.quiz(lessonId),
      flashcards: onDemandKeys.flashcards(lessonId),
      glossary: onDemandKeys.glossary(lessonId),
      video: ['lessons', 'video', lessonId],
      highlights: queryKeys.lessons.highlights(lessonId),
    })[type];

  const pipelineKey = queryKeys.lessons.pipelineStatus(lessonId);

  const mutation = useMutation<
    StepActionResponse,
    Error,
    OnDemandContentType,
    { prevContent: unknown; prevPipeline: unknown; key: readonly string[] }
  >({
    mutationFn: contentType => lessonApi.retryContent(lessonId, contentType),
    retry: retryUnlessConflict,
    onMutate: async contentType => {
      setPendingType(contentType);
      const key = contentKey(contentType);

      // Cancel in-flight refetches so a poll landing mid-mutation can't clobber
      // the optimistic value with the stale "failed" state.
      await queryClient.cancelQueries({ queryKey: key });
      await queryClient.cancelQueries({ queryKey: pipelineKey });

      const prevContent = queryClient.getQueryData(key);
      const prevPipeline = queryClient.getQueryData(pipelineKey);

      // Flip the artifact tile to "processing" INSTANTLY instead of waiting for
      // the next 3s poll. Only merge when data already exists (a panel showing
      // "retry" means it does), so we never fabricate a partial record. The poll
      // reconciles with server truth right after (which will also be processing).
      if (prevContent && typeof prevContent === 'object') {
        queryClient.setQueryData(key, {
          ...(prevContent as Record<string, unknown>),
          status: 'processing',
          error: null,
        });
      }

      // Mirror the flip onto the matching pipeline step so any pipeline-driven
      // UI (skeletons/badges) updates immediately too.
      if (prevPipeline && typeof prevPipeline === 'object') {
        const pipe = prevPipeline as PipelineStatusResponse;
        const step = pipe.steps?.[contentType];
        if (step) {
          queryClient.setQueryData(pipelineKey, {
            ...pipe,
            steps: {
              ...pipe.steps,
              [contentType]: {
                ...step,
                state: 'processing' as StepState,
                error: null,
              },
            },
          });
        }
      }

      return { prevContent, prevPipeline, key };
    },
    onError: (err, _contentType, ctx) => {
      // Retry POST failed - roll back so we don't leave a fake "processing".
      if (ctx) {
        queryClient.setQueryData(ctx.key, ctx.prevContent);
        queryClient.setQueryData(pipelineKey, ctx.prevPipeline);
      }
      // 409 = nothing to retry (already generated): refetch the real state.
      handleStepConflict(err, queryClient, lessonId, ctx ? [ctx.key] : []);
    },
    onSettled: () => {
      setPendingType(null);
    },
    onSuccess: (_data, contentType) => {
      queryClient.invalidateQueries({ queryKey: contentKey(contentType) });
      queryClient.invalidateQueries({ queryKey: pipelineKey });
    },
  });

  return { ...mutation, pendingType };
}
